import React from 'react';

interface EstimatedProfitReportProps {
  totalSales: number;
  completedSales: number;
  pendingSales: number;
  totalFlatRateEarnings: number;
  estimatedFlatRateEarnings: number;
  totalHourlyEarnings: number;
  estimatedHourlyEarnings: number;
  totalSuppliesCost: number;
  totalFixedExpenses: number;
}

export const EstimatedProfitReport: React.FC<EstimatedProfitReportProps> = ({
  totalSales,
  completedSales,
  pendingSales,
  totalFlatRateEarnings,
  estimatedFlatRateEarnings,
  totalHourlyEarnings,
  estimatedHourlyEarnings,
  totalSuppliesCost,
  totalFixedExpenses,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-5 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Estimated Profit</h2>
      </div>
      
      <div className="p-5 space-y-5">
        {/* Sales Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-900">Total Sales (excl. tax)</h3>
              <div className="text-2xl font-bold text-green-600">
                ${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1">Completed</div>
                <div className="text-base font-semibold text-green-600">
                  ${completedSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-1">Pending</div>
                <div className="text-base font-semibold text-yellow-600">
                  ${pendingSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Earnings Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Flat Rate Earnings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">Total Flat Rate Earnings</h3>
                <div className="text-lg font-bold text-red-600">
                  -${(totalFlatRateEarnings + estimatedFlatRateEarnings).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-md p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-1">Actual</div>
                  <div className="text-base font-semibold text-red-600">
                    -${totalFlatRateEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="bg-white rounded-md p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-1">Estimated</div>
                  <div className="text-base font-semibold text-blue-600">
                    -${estimatedFlatRateEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hourly Earnings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">Total Hourly Earnings</h3>
                <div className="text-lg font-bold text-red-600">
                  -${(totalHourlyEarnings + estimatedHourlyEarnings).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-white rounded-md p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-1">Actual</div>
                  <div className="text-base font-semibold text-red-600">
                    -${totalHourlyEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="bg-white rounded-md p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-1">Estimated</div>
                  <div className="text-base font-semibold text-blue-600">
                    -${estimatedHourlyEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Costs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-base font-medium text-gray-900 mb-2">Supplies Cost</h3>
              <div className="text-lg font-bold text-gray-900">
                -${totalSuppliesCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-base font-medium text-gray-900 mb-2">Fixed Expenses</h3>
              <div className="text-lg font-bold text-gray-900">
                -${totalFixedExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

        {/* Final Profit Section */}
        {(() => {
          const totalIncome = totalSales;
          const totalExpenses = (
            totalFlatRateEarnings +
            estimatedFlatRateEarnings +
            totalHourlyEarnings +
            estimatedHourlyEarnings +
            totalSuppliesCost +
            totalFixedExpenses
          );
          const profit = totalIncome - totalExpenses;
          
          return (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium text-gray-900">Final Estimated Profit</h3>
                  <div className={`text-2xl font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {profit >= 0 ? '+' : '-'}${Math.abs(profit).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-md p-3 border border-gray-200">
                    <div className="text-xs font-medium text-gray-600 mb-1">Total Income</div>
                    <div className="text-base font-semibold text-gray-900">
                      ${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-white rounded-md p-3 border border-gray-200">
                    <div className="text-xs font-medium text-gray-600 mb-1">Total Expenses</div>
                    <div className="text-base font-semibold text-gray-900">
                      -${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}; 